import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { useToast } from '../context/ToastContext';
import { ArrowLeft, Trash2, Plus, X, Phone, Mail, Tag, Download, ShieldAlert, Copy, Check, PhoneCall } from 'lucide-react';


// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('nb-NO', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function relativeTime(dateStr) {
  if (!dateStr) return '-';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Akkurat nå';
  if (mins < 60) return `${mins} min siden`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} t siden`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} d siden`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks} uke${weeks > 1 ? 'r' : ''} siden`;
  const months = Math.floor(days / 30);
  return `${months} mnd siden`;
}

function LeadBadge({ score }) {
  if (score == null) return <span className="text-slate-500 text-xs">-</span>;
  const cls =
    score >= 70 ? 'bg-green-500/20 text-green-400' :
    score >= 40 ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-red-500/20 text-red-400';
  return (
    <span className={`text-xs font-mono font-medium px-2 py-0.5 rounded ${cls}`}>
      {score}
    </span>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className="bg-navy-800 border border-navy-600 rounded-xl p-5">
      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{label}</p>
      <p className="text-2xl font-bold text-white font-heading">{value ?? '-'}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

// ─── Main ────────────────────────────────────────────────────────────────────

export default function ContactDetail() {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [contact, setContact] = useState(null);
  const [phoneCopied, setPhoneCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Editable fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [showTagInput, setShowTagInput] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const fetchContact = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/contacts/${contactId}`);
      setContact(data);
      setName(data.name || '');
      setEmail(data.email || '');
      setNotes(data.notes || '');
      setTags(data.tags || []);
    } catch (err) {
      setError('Kunne ikke laste kontakt');
      console.error(err);
      toast.error('Kunne ikke laste kontakt');
    } finally {
      setLoading(false);
    }
  }, [contactId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchContact(); }, [fetchContact]);

  const saveField = async (fields) => {
    setSaving(true);
    try {
      await api.put(`/api/contacts/${contactId}`, fields);
    } catch (err) {
      console.error('Save failed', err);
      toast.error('Kunne ikke lagre endringer');
    } finally {
      setSaving(false);
    }
  };

  const handleBlurName = () => saveField({ name, email, notes, tags });
  const handleBlurEmail = () => saveField({ name, email, notes, tags });
  const handleBlurNotes = () => saveField({ name, email, notes, tags });

  const addTag = async () => {
    const tag = newTag.trim();
    if (!tag || tags.includes(tag)) { setNewTag(''); setShowTagInput(false); return; }
    const updated = [...tags, tag];
    setTags(updated);
    setNewTag('');
    setShowTagInput(false);
    await saveField({ name, email, notes, tags: updated });
  };

  const removeTag = async (tag) => {
    const updated = tags.filter((t) => t !== tag);
    setTags(updated);
    await saveField({ name, email, notes, tags: updated });
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/api/contacts/${contactId}`);
      navigate('/contacts');
    } catch (err) {
      console.error('Delete failed', err);
      toast.error('Kunne ikke slette kontakt');
    }
  };

  const [gdprExporting, setGdprExporting] = useState(false);
  const [gdprDeleting, setGdprDeleting] = useState(false);
  const [gdprConfirm, setGdprConfirm] = useState(false);

  const copyPhone = async () => {
    const num = contact?.phone_number;
    if (!num) return;
    try {
      await navigator.clipboard.writeText(num);
      setPhoneCopied(true);
      setTimeout(() => setPhoneCopied(false), 2000);
    } catch {
      toast.error('Kunne ikke kopiere nummer');
    }
  };

  const handleGDPRExport = async () => {
    setGdprExporting(true);
    try {
      const response = await api.get(`/api/contacts/${contactId}/gdpr-export`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `gdpr-export-${contactId}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('GDPR-data eksportert');
    } catch (e) {
      console.error('GDPR export failed', e);
      toast.error('Kunne ikke eksportere GDPR-data');
    } finally {
      setGdprExporting(false);
    }
  };

  const handleGDPRDelete = async () => {
    setGdprDeleting(true);
    try {
      await api.delete(`/api/contacts/${contactId}/gdpr-delete`);
      navigate('/contacts');
    } catch (e) {
      console.error('GDPR delete failed', e);
      toast.error('Kunne ikke slette alle data');
      setGdprDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-navy-800 rounded w-32 mb-6" />
        <div className="h-40 bg-navy-800 rounded-xl" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-navy-800 rounded-xl" />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20 text-slate-500">
        <p>{error}</p>
        <button onClick={() => navigate('/contacts')} className="mt-4 text-accent-blue hover:underline text-sm">
          Tilbake til kontakter
        </button>
      </div>
    );
  }

  const callHistory = [...(contact?.call_history || [])].sort(
    (a, b) => new Date(b.created_at || b.started_at) - new Date(a.created_at || a.started_at)
  );

  const mostRequested = (() => {
    const freq = {};
    callHistory.forEach((c) => {
      const s = c.service_requested || c.call_intent;
      if (s) freq[s] = (freq[s] || 0) + 1;
    });
    return Object.entries(freq).sort((a, b) => b[1] - a[1])[0]?.[0] || '-';
  })();

  return (
    <div>
      {/* Back */}
      <button
        onClick={() => navigate('/contacts')}
        className="flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-6 transition-colors"
      >
        <ArrowLeft size={16} />
        Tilbake til kontakter
      </button>

      {/* Header card */}
      <div className="bg-navy-800 border border-navy-600 rounded-xl p-5 mb-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            {/* Name */}
            <div className="mb-4">
              <label className="block text-xs text-slate-500 uppercase tracking-wider mb-1">Navn</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={handleBlurName}
                className="bg-transparent border-b border-navy-600 focus:border-accent-blue px-0 py-1 text-xl font-bold text-white focus:outline-none w-full max-w-xs transition-colors"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              {/* Phone (read-only) */}
              <div>
                <label className="block text-xs text-slate-500 uppercase tracking-wider mb-1">
                  <Phone size={11} className="inline mr-1" />Telefon
                </label>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-white font-mono text-sm">{contact?.phone_number || '-'}</p>
                  {contact?.phone_number && (
                    <>
                      <button
                        onClick={copyPhone}
                        title="Kopier telefonnummer"
                        className="text-slate-500 hover:text-white transition-colors"
                        aria-label="Kopier telefonnummer"
                      >
                        {phoneCopied ? <Check size={13} className="text-accent-green" /> : <Copy size={13} />}
                      </button>
                      <a
                        href={`tel:${contact.phone_number}`}
                        title="Ring tilbake"
                        data-testid="contact-callback-link"
                        className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-accent-blue/15 text-accent-blue border border-accent-blue/30 hover:bg-accent-blue/25 transition-colors ml-1"
                      >
                        <PhoneCall size={11} /> Ring tilbake
                      </a>
                    </>
                  )}
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs text-slate-500 uppercase tracking-wider mb-1">
                  <Mail size={11} className="inline mr-1" />E-post
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={handleBlurEmail}
                  placeholder="ikke satt"
                  className="bg-transparent border-b border-navy-600 focus:border-accent-blue px-0 py-0.5 text-sm text-white placeholder-slate-600 focus:outline-none w-full transition-colors"
                />
              </div>
            </div>

            {/* Tags */}
            <div className="mb-4">
              <label className="block text-xs text-slate-500 uppercase tracking-wider mb-2">
                <Tag size={11} className="inline mr-1" />Tags
              </label>
              <div className="flex flex-wrap gap-2 items-center">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-navy-700 border border-navy-600 text-slate-300"
                  >
                    {tag}
                    <button onClick={() => removeTag(tag)} className="text-slate-500 hover:text-red-400 transition-colors ml-0.5">
                      <X size={11} />
                    </button>
                  </span>
                ))}
                {showTagInput ? (
                  <input
                    autoFocus
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') addTag(); if (e.key === 'Escape') { setShowTagInput(false); setNewTag(''); } }}
                    onBlur={addTag}
                    placeholder="Ny tag..."
                    className="text-xs bg-navy-700 border border-navy-600 rounded-full px-3 py-1 text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-accent-blue w-24"
                  />
                ) : (
                  <button
                    onClick={() => setShowTagInput(true)}
                    className="flex items-center gap-1 text-xs text-slate-500 hover:text-accent-blue border border-dashed border-navy-600 hover:border-accent-blue rounded-full px-2.5 py-1 transition-colors"
                  >
                    <Plus size={11} />
                    Legg til
                  </button>
                )}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-xs text-slate-500 uppercase tracking-wider mb-1">Notater</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onBlur={handleBlurNotes}
                rows={3}
                placeholder="Legg til notater..."
                className="w-full bg-navy-700 border border-navy-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue resize-none transition-colors"
              />
              {saving && <p className="text-xs text-slate-500 mt-1">Lagrer...</p>}
            </div>
          </div>

          {/* Right side: source + delete */}
          <div className="flex flex-col items-end gap-3 flex-shrink-0">
            {contact?.source && (
              <span className="text-xs px-2.5 py-1 rounded-full bg-accent-blue/15 text-accent-blue border border-accent-blue/30">
                {contact.source}
              </span>
            )}
            {!deleteConfirm ? (
              <button
                onClick={() => setDeleteConfirm(true)}
                className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-red-400 border border-navy-600 hover:border-red-500/50 px-3 py-1.5 rounded-lg transition-colors"
              >
                <Trash2 size={13} />
                Slett
              </button>
            ) : (
              <div className="flex flex-col items-end gap-2">
                <p className="text-xs text-red-400">Sikker?</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setDeleteConfirm(false)}
                    className="text-xs border border-navy-600 text-slate-400 px-2.5 py-1 rounded-lg hover:bg-navy-700 transition-colors"
                  >
                    Nei
                  </button>
                  <button
                    onClick={handleDelete}
                    className="text-xs bg-red-500/20 text-red-400 border border-red-500/30 px-2.5 py-1 rounded-lg hover:bg-red-500/30 transition-colors"
                  >
                    Slett
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* GDPR self-serve actions */}
      <div className="bg-navy-800 border border-navy-600 rounded-xl p-4 mb-6" data-testid="gdpr-actions">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-1">GDPR / Personvern</h3>
            <p className="text-xs text-slate-400 max-w-xl leading-relaxed">
              Last ned alle data om denne innringeren (kontakt, samtaler, SMS) som JSON, eller slett dem permanent. Sletting kan ikke angres.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={handleGDPRExport}
              disabled={gdprExporting}
              data-testid="gdpr-export-btn"
              className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-navy-600 text-slate-300 hover:border-accent-blue hover:text-white transition-colors disabled:opacity-50"
            >
              <Download size={13} />
              {gdprExporting ? 'Eksporterer...' : 'Eksporter alle data (JSON)'}
            </button>
            {!gdprConfirm ? (
              <button
                onClick={() => setGdprConfirm(true)}
                data-testid="gdpr-delete-btn"
                className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors"
              >
                <ShieldAlert size={13} />
                Slett all data
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-xs text-red-400">Slette permanent?</span>
                <button
                  onClick={() => setGdprConfirm(false)}
                  disabled={gdprDeleting}
                  className="text-xs border border-navy-600 text-slate-400 px-2 py-1 rounded hover:bg-navy-700 transition-colors"
                >
                  Avbryt
                </button>
                <button
                  onClick={handleGDPRDelete}
                  disabled={gdprDeleting}
                  className="text-xs bg-red-500/20 text-red-400 border border-red-500/30 px-2 py-1 rounded hover:bg-red-500/30 transition-colors disabled:opacity-50"
                >
                  {gdprDeleting ? 'Sletter...' : 'Ja, slett alt'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total samtaler" value={contact?.call_count ?? callHistory.length} />
        <StatCard label="Gj.snitt lead score" value={contact?.avg_lead_score != null ? Math.round(contact.avg_lead_score) : '-'} />
        <StatCard
          label="Siste samtale"
          value={contact?.last_call_at ? relativeTime(contact.last_call_at) : '-'}
        />
        <StatCard label="Mest etterspurte tjeneste" value={mostRequested} />
      </div>

      {/* Call history */}
      <div>
        <h2 className="font-heading text-base font-semibold text-white mb-3">Samtalehistorikk</h2>
        {callHistory.length === 0 ? (
          <div data-testid="contact-no-calls" className="bg-navy-800 border border-navy-600 rounded-xl p-10 text-center">
            <Phone size={28} className="mx-auto mb-3 text-slate-600" />
            <p className="text-sm font-medium text-slate-300">Ingen samtaler ennå</p>
            <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">Når denne kontakten ringer AI-resepsjonisten din, vises hver samtale her med transkripsjon, intensjon og lead score.</p>
          </div>
        ) : (
          <div className="bg-navy-800 border border-navy-600 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-navy-600">
                    <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Dato</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Henvendelse</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Lead score</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Sammendrag</th>
                  </tr>
                </thead>
                <tbody>
                  {callHistory.map((call) => {
                    const callId = call.call_id || call._id;
                    const score = call.lead_score ?? call.call_analysis?.lead_score;
                    const intent = call.call_intent || call.call_analysis?.call_intent || '-';
                    const summary = call.call_summary || call.call_analysis?.call_summary || '';
                    const date = call.created_at || call.started_at;
                    return (
                      <tr
                        key={callId}
                        onClick={() => callId && navigate(`/calls/${callId}`)}
                        className={`hover:bg-navy-700 transition-colors border-b border-navy-700 last:border-0 ${callId ? 'cursor-pointer' : ''}`}
                      >
                        <td className="px-4 py-3.5 text-sm text-slate-400 whitespace-nowrap">{formatDate(date)}</td>
                        <td className="px-4 py-3.5">
                          <span className="text-xs px-2 py-1 rounded bg-navy-700 text-slate-300">{intent}</span>
                        </td>
                        <td className="px-4 py-3.5">
                          <LeadBadge score={score} />
                        </td>
                        <td className="px-4 py-3.5 text-sm text-slate-400 max-w-xs truncate">{summary || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
